import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '../lib/api.js';
import type { CreateQuoteBody } from '../lib/quotes.js';

// Response shapes for the 7a quotes endpoints. Declared here (web-side) because
// the API keeps these DTOs feature-local; promoting them to @admitly/shared would
// mean editing a backend file, which this brief must not do. They mirror
// apps/api/src/features/quotes/quotes.service.ts exactly.
//
// NOT hooks/useQuote.ts — that is the untouched Phase-8 student cost estimator,
// a SIBLING feature at /api/v1/quote that happens to share the word. No export
// name collides (it exports useQuotePreview / useSendQuote).

/**
 * One LOCKED line. `feeCents` and `netFeeCents` are NON-nullable: a course with
 * no agency fee is rejected at create, so a saved line always carries a number.
 */
export type QuoteLineView = {
  id: string;
  masterCourseId: string | null;
  lineOrder: number;
  courseName: string;
  providerName: string;
  cricosCode: string | null;
  feeCents: number;
  /**
   * 'none' = a version that ENDED the scholarship; 'not_set' = there was never
   * one as of the quote date. Both mean "no discount in force", but they are
   * different facts.
   */
  scholarshipKind: 'percent' | 'amount' | 'none' | 'not_set';
  scholarshipPercentBp: number | null;
  scholarshipAmountCents: number | null;
  netFeeCents: number;
};

export type QuoteView = {
  id: string;
  quoteNumber: string;
  quoteDate: string; // YYYY-MM-DD — the as-of date EVERY line locked on
  client: {
    name: string;
    email: string;
    phone: string | null;
    visaSubclass: string;
    nationality: string | null;
  };
  lines: QuoteLineView[];
  totalNetFeeCents: number;
  notes: string | null;
  createdBy: string | null;
  createdByName: string | null;
  createdAt: string; // ISO 8601
};

/**
 * The list row. NOT a subset of QuoteView — the client is FLAT here
 * (`clientName`) and NESTED there (`client.name`), and there is NO `lines[]`.
 * Two distinct types on purpose; do not derive one from the other, and do not
 * try to sum line nets on the list — render the server's totalNetFeeCents.
 */
export type QuoteListRow = {
  id: string;
  quoteNumber: string;
  quoteDate: string;
  clientName: string;
  clientEmail: string;
  clientVisaSubclass: string;
  lineCount: number;
  totalNetFeeCents: number;
  createdBy: string | null;
  createdByName: string | null;
  createdAt: string;
};

/** The list endpoint returns this shape directly — there is no wrapper key. */
export type QuoteListResponse = { items: QuoteListRow[] };

const quotesKey = ['quotes'] as const;
const quoteKey = (quoteId: string) => [...quotesKey, quoteId] as const;

/** Every quote in the tenant, newest first. Tenant-wide, gated on create_quotes. */
export function useQuotes() {
  return useQuery({
    queryKey: quotesKey,
    queryFn: () => apiGet<QuoteListResponse>('/quotes'),
    staleTime: 30_000,
  });
}

/** One locked quote. Disabled until an id is known. */
export function useQuote(quoteId: string | null) {
  return useQuery({
    queryKey: quoteId ? quoteKey(quoteId) : [...quotesKey, '__none__'],
    queryFn: () => apiGet<{ quote: QuoteView }>(`/quotes/${quoteId}`),
    enabled: !!quoteId,
  });
}

/**
 * Build and permanently lock a quote.
 *
 * There is no update hook and no delete hook, and there never will be: the API
 * exposes only POST / GET / GET :id. A quote is a permanent record.
 *
 * The 201 body IS the authoritative locked quote, so it seeds the detail cache
 * directly — the screen it navigates to needs no second round-trip and cannot
 * flash a stale figure.
 *
 * Navigation deliberately lives in the route wrapper, not here: useNavigate()
 * closes over a router that is undefined outside a RouterProvider, and this
 * repo has no router in its tests.
 */
export function useCreateQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateQuoteBody) => apiPost<{ quote: QuoteView }>('/quotes', body),
    onSuccess: ({ quote }) => {
      qc.invalidateQueries({ queryKey: quotesKey });
      qc.setQueryData(quoteKey(quote.id), { quote });
    },
  });
}

/**
 * Deliver a frozen quote to a client as a branded PDF by email (Brief 10).
 *
 * NOT hooks/useQuote.ts's useSendQuote — that is the untouched Phase-8 student
 * cost estimator (/api/v1/quote/send). This hits /api/v1/quotes/:id/send, the
 * packaged-quote sibling. The payload carries a recipient and optional overrides
 * and NOTHING else: no price, no course, and — deliberately — no commission
 * field anywhere, because the client-facing quote never exposes it.
 */
export type SendPackagedQuotePayload = {
  quoteId: string;
  recipientEmail: string;
  recipientName?: string;
  subjectOverride?: string;
  bodyOverride?: string;
  ccEmails?: string[];
};

export type SendPackagedQuoteResponse = { ok: true; id: string; messageId: string };

export function useSendPackagedQuote() {
  return useMutation<SendPackagedQuoteResponse, Error, SendPackagedQuotePayload>({
    mutationFn: ({ quoteId, recipientEmail, recipientName, subjectOverride, bodyOverride, ccEmails }) =>
      apiPost<SendPackagedQuoteResponse>(`/quotes/${quoteId}/send`, {
        recipientEmail: recipientEmail.trim(),
        recipientName: recipientName?.trim() || undefined,
        subjectOverride: subjectOverride?.trim() || undefined,
        bodyOverride: bodyOverride?.trim() || undefined,
        ccEmails: ccEmails && ccEmails.length > 0 ? ccEmails : undefined,
      }),
  });
}
